import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { PHASES } from "@/components/Stepper";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FolderKanban, Plus, TrendingUp, Activity, CheckCircle2, ArrowRight } from "lucide-react";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).finally(() => setLoading(false));
  }, []);

  const active = projects.filter((p) => p.status === "active").length;
  const completed = projects.filter((p) => p.status === "completed").length;

  const stats = [
    { label: "Total Projects", value: projects.length, icon: FolderKanban },
    { label: "Active", value: active, icon: Activity },
    { label: "Completed", value: completed, icon: CheckCircle2 },
  ];

  return (
    <Layout>
      <header className="h-16 flex items-center justify-between px-6 border-b border-slate-200 bg-white/70 backdrop-blur-xl z-20 sticky top-0">
        <h1 className="font-display text-xl font-bold text-slate-900">Dashboard</h1>
        <button
          data-testid="new-project-dashboard-btn"
          onClick={() => navigate("/projects?new=1")}
          className="bg-emerald-500 text-white hover:bg-emerald-600 px-4 py-2 rounded-md font-medium transition-colors flex items-center gap-2 text-sm"
        >
          <Plus className="h-4 w-4" /> New Project
        </button>
      </header>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="rounded-xl bg-slate-900 relative overflow-hidden mb-6">
          <img
            src="https://images.unsplash.com/photo-1704975986930-0c09f513c985?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-25"
          />
          <div className="relative p-8">
            <p className="text-emerald-400 text-xs uppercase tracking-[0.2em] font-semibold">Welcome back</p>
            <h2 className="font-display text-3xl font-bold text-white mt-2">{user?.name?.split(" ")[0] || "Consultant"}</h2>
            <p className="text-slate-300 mt-2 max-w-lg">
              Drive measurable results with the ATD 6-Phase HPI model. Diagnose causes before prescribing solutions.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {stats.map((s) => (
            <div key={s.label} className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid={`stat-${s.label}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">{s.label}</span>
                <s.icon className="h-5 w-5 text-emerald-500" />
              </div>
              <p className="font-display text-3xl font-bold text-slate-900 mt-2">{s.value}</p>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-semibold text-slate-900">Recent Projects</h3>
        </div>

        {loading ? (
          <p className="text-slate-400 text-sm">Loading…</p>
        ) : projects.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-lg p-12 text-center">
            <FolderKanban className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">No projects yet</p>
            <p className="text-slate-400 text-sm mt-1">Create your first HPI consulting project to get started.</p>
            <button
              data-testid="empty-new-project-btn"
              onClick={() => navigate("/projects?new=1")}
              className="mt-5 bg-emerald-500 text-white hover:bg-emerald-600 px-4 py-2 rounded-md font-medium text-sm inline-flex items-center gap-2"
            >
              <Plus className="h-4 w-4" /> New Project
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.slice(0, 6).map((p) => (
              <button
                key={p.id}
                data-testid={`project-card-${p.id}`}
                onClick={() => navigate(`/projects/${p.id}`)}
                className="text-left bg-white border border-slate-200 rounded-lg p-5 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all group"
              >
                <div className="flex items-center justify-between">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.status === "completed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                    {p.status}
                  </span>
                  <span className="text-xs text-slate-400">Phase {p.current_phase}/6</span>
                </div>
                <h4 className="font-display font-semibold text-slate-900 mt-3 text-lg">{p.name}</h4>
                <p className="text-sm text-slate-500 line-clamp-2 mt-1">{p.description || PHASES[p.current_phase - 1]?.title}</p>
                <div className="mt-4 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 transition-all" style={{ width: `${(p.current_phase / 6) * 100}%` }} />
                </div>
                <div className="mt-4 text-emerald-600 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                  Open <ArrowRight className="h-4 w-4" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
